import {
  Container,
  ChevronIcon,
  ChevronButton,
  BreadcrumbsTrigger,
  BreadcrumbsTriggerText,
  ChevronUpDownIcon,
  DropdownContainer,
  DropdownItem,
} from './ProjectsBreadcrumbsDropdown.styles'
import React from 'react'
import { BreadcrumbsData } from '../../types'
import { ProjectIcon } from '../../components/ProjectIcon'
import { Dropdown, WorkspaceLogo, Tooltip } from '@sketch/components'
import { Link } from 'react-router-dom'
import { routes } from '@sketch/modules-common'
import { icons as Icons } from '../../../workspace/components/Sidebar/icons'
import { isTruthy } from '@sketch/utils'

export interface ProjectsBreadcrumbsDropdownProps {
  data: BreadcrumbsData | undefined
}

interface ProjectsBreadcrumbsDropdownItem {
  to: string
  name: string
  depth?: number
  current?: boolean
  icon: React.ReactNode
}

interface BackToProps {
  tooltip: string
  to: string
  name: string
}

const getBackToData = (
  data: BreadcrumbsData | undefined
): BackToProps | null => {
  if (!data?.currentProject) return null
  const { parentProjects, workspace, currentProject } = data

  const parentProject = parentProjects[0]

  const workspaceId = workspace.identifier

  if (!parentProject) {
    if (currentProject.archivedAt) {
      return {
        tooltip: 'Archive',
        to: routes.WORKSPACE_ARCHIVE.create({ workspaceId }),
        name: 'Archive',
      }
    }
    if (currentProject.deletedAt) {
      return {
        tooltip: 'Trash',
        to: routes.WORKSPACE_TRASH.create({ workspaceId }),
        name: 'Trash',
      }
    }

    return null
  }

  const projectId = parentProject.identifier
  return {
    tooltip: 'Folder',
    to: routes.WORKSPACE_PROJECT.create({ workspaceId, projectId }),
    name: parentProject.name,
  }
}

const getBreadcrumbsData = (data: BreadcrumbsData | undefined) => {
  if (!data?.currentProject) return []
  const { parentProjects, siblingProjects, workspace, currentProject } = data

  const workspaceId = workspace.identifier
  const isStashedProject = currentProject.archivedAt || currentProject.deletedAt
  const maxDepth = parentProjects.length

  const breadcrumbsRaw: (ProjectsBreadcrumbsDropdownItem | null)[] = [
    {
      to: routes.WORKSPACE_SHARES.create({ workspaceId }),
      name: workspace.name,
      icon: (
        <WorkspaceLogo
          size="17px"
          workspaceName={workspace.name}
          src={workspace.avatar?.small || workspace.avatar?.large}
        />
      ),
    },
    currentProject.archivedAt
      ? {
          to: routes.WORKSPACE_ARCHIVE.create({ workspaceId }),
          name: 'Archive',
          depth: 1,
          icon: <Icons.Archive />,
        }
      : null,
    currentProject.deletedAt
      ? {
          to: routes.WORKSPACE_TRASH.create({ workspaceId }),
          name: 'Trash',
          depth: 1,
          icon: <Icons.Trash />,
        }
      : null,
    ...[...parentProjects].reverse().map((project, index) => {
      const projectId = project.identifier
      return {
        to: routes.WORKSPACE_PROJECT.create({ workspaceId, projectId }),
        name: project.name,
        depth: index + (isStashedProject ? 2 : 1),
        icon: <ProjectIcon project={project} isOpen={true} />,
      }
    }),
    ...siblingProjects.map(project => {
      const projectId = project.identifier
      return {
        to: routes.WORKSPACE_PROJECT.create({ workspaceId, projectId }),
        name: project.name,
        current: projectId === currentProject.identifier,
        depth: maxDepth + (isStashedProject ? 2 : 1),
        icon: <ProjectIcon project={project} isOpen={false} />,
      }
    }),
  ]
  return breadcrumbsRaw.filter(isTruthy)
}

export const ProjectsBreadcrumbsDropdown = (
  props: ProjectsBreadcrumbsDropdownProps
) => {
  const { data } = props

  const backTo = getBackToData(data)
  if (!backTo) return null

  const breadcrumbs = getBreadcrumbsData(data)

  return (
    <Container>
      <Tooltip content={`Back to ${backTo.tooltip}`} placement="top-start">
        <Link to={backTo.to}>
          <ChevronButton>
            <ChevronIcon />
          </ChevronButton>
        </Link>
      </Tooltip>
      <Dropdown
        placement="bottom-start"
        toggle={
          <BreadcrumbsTrigger>
            <BreadcrumbsTriggerText>{backTo.name}</BreadcrumbsTriggerText>
            <ChevronUpDownIcon />
          </BreadcrumbsTrigger>
        }
      >
        <DropdownContainer>
          {breadcrumbs.map(({ to, icon, depth, name, current }, index) => (
            <Link to={to} key={index}>
              <DropdownItem
                icon={icon}
                depth={depth}
                aria-current={current || undefined}
              >
                {name}
              </DropdownItem>
            </Link>
          ))}
        </DropdownContainer>
      </Dropdown>
    </Container>
  )
}
